<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
    offers: {
        type: Array,
        default: () => [],
    },
})

const sortKey = ref('price')
const sortDirection = ref('asc')

const sortedOffers = computed(() => [...props.offers].sort((left, right) => {
    let leftValue = left[sortKey.value]
    let rightValue = right[sortKey.value]

    if (['price', 'available_quantity'].includes(sortKey.value)) {
        leftValue = leftValue === null ? Number.POSITIVE_INFINITY : Number(leftValue)
        rightValue = rightValue === null ? Number.POSITIVE_INFINITY : Number(rightValue)
    } else {
        leftValue = String(leftValue || '').toLocaleLowerCase()
        rightValue = String(rightValue || '').toLocaleLowerCase()
    }

    if (leftValue === rightValue) return 0
    const result = leftValue > rightValue ? 1 : -1
    return sortDirection.value === 'asc' ? result : -result
}))

const sortBy = key => {
    if (sortKey.value === key) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
    } else {
        sortKey.value = key
        sortDirection.value = 'asc'
    }
}

const sortIcon = key => sortKey.value === key
    ? (sortDirection.value === 'asc' ? '↑' : '↓')
    : '↕'

const formatPrice = offer => {
    const value = Number(offer.price)
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: offer.currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
    }).format(value)
}

const unitLabels = {
    g: ['g', 'g'],
    kg: ['kg', 'kg'],
    lb: ['lb', 'lb'],
    arroba: ['arroba', 'arrobas'],
    quintal: ['quintal', 'quintales'],
    ton: ['tonelada', 'toneladas'],
    unidad: ['unidad', 'unidades'],
    docena: ['docena', 'docenas'],
    ciento: ['ciento', 'cientos'],
    racimo: ['racimo', 'racimos'],
    manojo: ['manojo', 'manojos'],
    atado: ['atado', 'atados'],
    bandeja: ['bandeja', 'bandejas'],
    bolsa: ['bolsa', 'bolsas'],
    paquete: ['paquete', 'paquetes'],
    caja: ['caja', 'cajas'],
    canastilla: ['canastilla', 'canastillas'],
    bulto: ['bulto', 'bultos'],
    saco: ['saco', 'sacos'],
    litro: ['litro', 'litros'],
    galon: ['galón', 'galones'],
}
const unitLabel = (unit, quantity = 1) => {
    const labels = unitLabels[unit]
    if (!labels) return unit
    return Number(quantity) === 1 ? labels[0] : labels[1]
}

const formatQuantity = offer => offer.available_quantity === null
    ? 'Consultar'
    : `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(Number(offer.available_quantity))} ${unitLabel(offer.unit, offer.available_quantity)}`

const formatLocation = offer => [offer.municipality, offer.department].filter(Boolean).join(', ')
const formatDate = date => new Intl.DateTimeFormat('es-CO', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${date}T00:00:00Z`))
const contactLabel = method => ({ whatsapp: 'WhatsApp', phone: 'Llamar', email: 'Correo' }[method] || 'Contactar')
</script>

<template>
    <section
        v-if="offers.length === 0"
        class="flex items-center justify-between gap-3 px-5 py-4 mt-5 border rounded-2xl border-light_gray bg-light_yellow"
        aria-label="Ofertas de productores"
    >
        <h2 class="text-sm font-bold tracking-wider uppercase text-enriqueto_green">
            Ofertas de productores
        </h2>
        <span class="inline-flex px-4 py-2 text-sm font-bold rounded-full bg-enriqueto_green text-white">
            0 ofertas publicadas
        </span>
    </section>

    <section v-else class="mt-10 overflow-hidden border shadow-sm rounded-3xl border-light_gray bg-white">
        <header class="p-5 md:p-7 bg-light_yellow">
            <div class="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <h2 class="text-sm font-bold tracking-wider uppercase text-enriqueto_green">Ofertas de productores</h2>
                    <p class="max-w-3xl mt-2 text-sm leading-relaxed text-gray">
                        Estos precios son definidos libremente por cada productor y son independientes del precio internacional de referencia mostrado arriba.
                    </p>
                </div>
                <span class="self-start px-4 py-2 text-sm font-bold rounded-full md:self-auto bg-enriqueto_green text-white">
                    {{ offers.length }} {{ offers.length === 1 ? 'oferta publicada' : 'ofertas publicadas' }}
                </span>
            </div>
        </header>

        <div>
            <div class="hidden overflow-x-auto md:block">
                <table class="w-full text-sm text-left">
                    <thead class="border-b bg-white border-light_gray text-enriqueto_green">
                        <tr>
                            <th class="px-5 py-4 font-bold">Productor</th>
                            <th class="px-5 py-4 font-bold">
                                <button type="button" @click="sortBy('price')">Precio {{ sortIcon('price') }}</button>
                            </th>
                            <th class="px-5 py-4 font-bold">
                                <button type="button" @click="sortBy('available_quantity')">Cantidad {{ sortIcon('available_quantity') }}</button>
                            </th>
                            <th class="px-5 py-4 font-bold">
                                <button type="button" @click="sortBy('municipality')">Ubicación {{ sortIcon('municipality') }}</button>
                            </th>
                            <th class="px-5 py-4 font-bold">
                                <button type="button" @click="sortBy('available_until')">Disponible hasta {{ sortIcon('available_until') }}</button>
                            </th>
                            <th class="px-5 py-4 font-bold">Contacto</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="offer in sortedOffers" :key="offer.id" class="border-b last:border-b-0 border-light_gray hover:bg-light_yellow">
                            <td class="px-5 py-4 font-medium text-enriqueto_green">
                                {{ offer.producer }}
                                <p v-if="offer.commercial_notes" class="max-w-xs mt-1 text-xs font-normal leading-relaxed text-gray">{{ offer.commercial_notes }}</p>
                            </td>
                            <td class="px-5 py-4 whitespace-nowrap">
                                <strong>{{ offer.currency }} {{ formatPrice(offer) }}</strong>
                                <span class="text-gray"> / {{ unitLabel(offer.unit) }}</span>
                            </td>
                            <td class="px-5 py-4 whitespace-nowrap">{{ formatQuantity(offer) }}</td>
                            <td class="px-5 py-4">{{ formatLocation(offer) }}</td>
                            <td class="px-5 py-4 whitespace-nowrap">{{ formatDate(offer.available_until) }}</td>
                            <td class="px-5 py-4">
                                <a :href="offer.contact_href" :target="offer.contact_method === 'whatsapp' ? '_blank' : null" rel="noopener" class="inline-flex items-center gap-1 px-4 py-2 font-bold text-white rounded-full bg-enriqueto_green">
                                    {{ contactLabel(offer.contact_method) }}
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="grid gap-4 p-4 md:hidden bg-light_yellow">
                <article v-for="offer in sortedOffers" :key="offer.id" class="p-5 bg-white border shadow-sm rounded-2xl border-light_gray">
                    <div class="flex items-start justify-between gap-4">
                        <div>
                            <p class="text-xs font-bold tracking-wider uppercase text-light_green">{{ offer.producer }}</p>
                            <h3 class="mt-1 text-xl font-bold text-enriqueto_green">{{ offer.currency }} {{ formatPrice(offer) }} <span class="text-sm font-normal text-gray">/ {{ unitLabel(offer.unit) }}</span></h3>
                        </div>
                        <span class="material-symbols-outlined text-light_green">storefront</span>
                    </div>
                    <dl class="grid grid-cols-2 gap-3 mt-5 text-sm">
                        <div><dt class="text-xs text-gray">Cantidad</dt><dd class="mt-1 font-medium">{{ formatQuantity(offer) }}</dd></div>
                        <div><dt class="text-xs text-gray">Ubicación</dt><dd class="mt-1 font-medium">{{ formatLocation(offer) }}</dd></div>
                        <div class="col-span-2"><dt class="text-xs text-gray">Disponible</dt><dd class="mt-1 font-medium">{{ formatDate(offer.available_from) }} – {{ formatDate(offer.available_until) }}</dd></div>
                    </dl>
                    <p v-if="offer.commercial_notes" class="mt-4 text-sm leading-relaxed text-gray">{{ offer.commercial_notes }}</p>
                    <a :href="offer.contact_href" :target="offer.contact_method === 'whatsapp' ? '_blank' : null" rel="noopener" class="block w-full px-4 py-3 mt-5 font-bold text-center text-white rounded-xl bg-enriqueto_green">
                        {{ contactLabel(offer.contact_method) }} al productor
                    </a>
                </article>
            </div>
        </div>
    </section>
</template>
